import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, Leaf, MessageCircle, Send, ShoppingCart, FileText, ArrowRight } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { ordersAPI } from '../../lib/api';
import { formatPrice } from '../../lib/utils';
import { getPackageDisplay, getPricePerUnit } from '../../lib/packageUtils';
import { toast } from 'sonner';

const CartItem = ({ item, updateQuantity, removeItem, showPrice, calcItemTotal }) => (
    <div className="flex gap-3 bg-white/60 p-3 rounded-xl" data-testid={`cart-item-${item.product_id}`}>
        <img src={item.image_url} alt={item.product_name} className="w-14 h-14 object-cover rounded-lg" />
        <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm text-jungle truncate">{item.product_name}</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
                {showPrice && item.price > 0
                    ? `${formatPrice(item.price)} / ${item.unit}`
                    : item.unit
                }
            </p>
            {item.package_weight > 0 && (
                <p className="text-xs text-amber-600 mt-0.5" data-testid={`package-weight-${item.product_id}`}>
                    {getPackageDisplay(item.package_weight, item.unit)}/colis &middot; {item.quantity} x {getPackageDisplay(item.package_weight, item.unit)} x {formatPrice(item.price)}{getPricePerUnit(item.unit)}
                </p>
            )}
            <div className="flex items-center gap-2 mt-2">
                <Button variant="outline" size="icon" className="h-7 w-7 border-jungle/20 rounded-lg"
                    onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                    data-testid={`decrease-${item.product_id}`}>
                    <Minus className="h-3 w-3" />
                </Button>
                <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                <Button variant="outline" size="icon" className="h-7 w-7 border-jungle/20 rounded-lg"
                    onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                    data-testid={`increase-${item.product_id}`}>
                    <Plus className="h-3 w-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:bg-destructive/10 rounded-lg"
                    onClick={() => removeItem(item.product_id)}
                    data-testid={`remove-${item.product_id}`}>
                    <Trash2 className="h-4 w-4" />
                </Button>
            </div>
        </div>
        {showPrice && item.price > 0 && (
            <div className="text-sm font-semibold text-leaf whitespace-nowrap">
                {formatPrice(calcItemTotal ? calcItemTotal(item) : item.price * item.quantity)}
            </div>
        )}
    </div>
);

const EmptyCart = ({ icon: Icon, message, closeCart, navigate }) => (
    <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
        <div className="w-20 h-20 rounded-2xl bg-jungle/5 flex items-center justify-center mb-6">
            <Icon className="w-10 h-10 text-jungle/30" />
        </div>
        <h3 className="font-heading font-semibold text-lg text-jungle mb-2">Panier vide</h3>
        <p className="text-muted-foreground text-sm">{message}</p>
        <Button
            onClick={() => { closeCart(); navigate('/catalogue'); }}
            className="mt-8 bg-leaf hover:bg-leaf/90 text-jungle rounded-xl"
            data-testid="browse-catalog-button"
        >
            Voir le catalogue
        </Button>
    </div>
);

export const CartDrawer = () => {
    const {
        orderItems, quoteItems,
        isOrderCartOpen, isQuoteCartOpen,
        closeOrderCart, closeQuoteCart,
        updateQuantity, removeItem,
        clearOrderItems, clearQuoteItems,
        orderTotal, orderCount, quoteCount,
        calcItemTotal,
    } = useCart();
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();

    const handleGoCheckout = () => {
        closeOrderCart();
        navigate('/commander');
    };

    const handleQuoteRequest = async () => {
        if (!isAuthenticated || !user) {
            closeQuoteCart();
            navigate('/connexion');
            return;
        }
        try {
            const quoteData = {
                items: quoteItems.map(item => ({
                    product_id: item.product_id,
                    product_name: item.product_name,
                    quantity: item.quantity,
                    unit: item.unit,
                })),
                delivery_address: user?.address || '',
                notes: 'Demande de prix',
            };
            const res = await ordersAPI.requestQuote(quoteData);
            clearQuoteItems();
            closeQuoteCart();
            toast.success(`Demande de prix ${res.data.quote_number} envoyee ! Vous recevrez une reponse rapidement.`);
        } catch {
            toast.error("Erreur lors de l'envoi de la demande");
        }
    };

    const handleWhatsAppQuote = handleQuoteRequest;

    return (
        <>
            {/* ========== ORDER CART DRAWER ========== */}
            <Sheet open={isOrderCartOpen} onOpenChange={(open) => { if (!open) closeOrderCart(); }}>
                <SheetContent className="w-full sm:max-w-lg flex flex-col bg-sand" data-testid="order-cart-drawer">
                    <SheetHeader>
                        <SheetTitle className="flex items-center gap-3 font-heading">
                            <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                                <ShoppingCart className="w-5 h-5 text-emerald-700" />
                            </div>
                            <div>
                                <span>Panier Commande</span>
                                {orderCount > 0 && (
                                    <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-xs">{orderCount} article{orderCount > 1 ? 's' : ''}</Badge>
                                )}
                            </div>
                        </SheetTitle>
                    </SheetHeader>

                    {orderItems.length === 0 ? (
                        <EmptyCart
                            icon={ShoppingCart}
                            message="Ajoutez des produits prets a commander"
                            closeCart={closeOrderCart}
                            navigate={navigate}
                        />
                    ) : (
                        <>
                            <ScrollArea className="flex-1 -mx-6 px-6">
                                <div className="space-y-3 py-4">
                                    {orderItems.map((item) => (
                                        <CartItem key={item.product_id} item={item} updateQuantity={updateQuantity} removeItem={removeItem} showPrice={true} calcItemTotal={calcItemTotal} />
                                    ))}
                                </div>
                            </ScrollArea>
                            <div className="border-t border-jungle/10 pt-4 space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-muted-foreground">{orderItems.length} article{orderItems.length > 1 ? 's' : ''}</span>
                                    <span className="font-heading font-bold text-xl text-leaf" data-testid="order-total">{formatPrice(orderTotal)}</span>
                                </div>
                                <Button
                                    onClick={handleGoCheckout}
                                    className="w-full bg-jungle hover:bg-jungle/90 text-sand h-12 rounded-xl"
                                    data-testid="go-checkout-button"
                                >
                                    <ArrowRight className="mr-2 h-4 w-4" />
                                    Passer la commande
                                </Button>
                            </div>
                        </>
                    )}
                </SheetContent>
            </Sheet>

            {/* ========== QUOTE CART DRAWER ========== */}
            <Sheet open={isQuoteCartOpen} onOpenChange={(open) => { if (!open) closeQuoteCart(); }}>
                <SheetContent className="w-full sm:max-w-lg flex flex-col bg-sand" data-testid="quote-cart-drawer">
                    <SheetHeader>
                        <SheetTitle className="flex items-center gap-3 font-heading">
                            <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                                <FileText className="w-5 h-5 text-amber-700" />
                            </div>
                            <div>
                                <span>Panier Demande de Prix</span>
                                {quoteCount > 0 && (
                                    <Badge className="ml-2 bg-amber-100 text-amber-700 text-xs">{quoteCount} produit{quoteCount > 1 ? 's' : ''}</Badge>
                                )}
                            </div>
                        </SheetTitle>
                    </SheetHeader>

                    {quoteItems.length === 0 ? (
                        <EmptyCart
                            icon={FileText}
                            message="Ajoutez des produits pour demander un prix"
                            closeCart={closeQuoteCart}
                            navigate={navigate}
                        />
                    ) : (
                        <>
                            <ScrollArea className="flex-1 -mx-6 px-6">
                                <div className="space-y-3 py-4">
                                    {quoteItems.map((item) => (
                                        <CartItem key={item.product_id} item={item} updateQuantity={updateQuantity} removeItem={removeItem} showPrice={false} />
                                    ))}
                                </div>
                            </ScrollArea>
                            <div className="border-t border-jungle/10 pt-4 space-y-3">
                                <div className="text-center">
                                    <p className="text-sm text-muted-foreground">
                                        {quoteItems.length} produit{quoteItems.length > 1 ? 's' : ''} - Prix sur demande
                                    </p>
                                </div>
                                <Button
                                    onClick={handleQuoteRequest}
                                    className="w-full bg-amber-600 hover:bg-amber-700 text-white h-12 rounded-xl"
                                    data-testid="send-quote-request-button"
                                >
                                    <Send className="mr-2 h-4 w-4" />
                                    Envoyer la demande de prix
                                </Button>
                            </div>
                        </>
                    )}
                </SheetContent>
            </Sheet>
        </>
    );
};
